/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2016, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./Pivot',
	'../grid/GridDnDManager',
	'underscore',
	'../grid/GridDataProvider',
	'../grid/GridDropTarget',
	'../grid/GridContentRenderer',
	'react-dom',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'react-draggable',
	'jquery',
	'../../../util/DashboardFormatter',
	'./CrosstabGridCellRenderers',
	'../localVis/grid/GridCellSelection',
	'../common/PrintHelper',
	'./helper/GridCellColRowSpanHelper',
	'./helper/GridCellExpansionHelper',
	'./CrosstabConstants'
], (
	Pivot,
	GridDndManager,
	_,
	DataProvider,
	GridDropTarget,
	GridContentRenderer,
	ReactDOM,
	React,
	Toolkit,
	Draggable,
	$,
	Formatter,
	CrosstabGridCellRenderers,
	GridCellSelection,
	PrintHelper,
	GridCellColRowSpanHelper,
	GridCellExpansionHelper,
	Constants
) => {
	'use strict';

	return class CrosstabGrid {

		constructor(options) {
			options = options || {};
			this.$el = options.$el;
			this.dashboardApi = options.dashboardApi;
			this.crosstabView = options.crosstabView;
			this.content = this.crosstabView && this.crosstabView.content;
			this.colorsService = options.colorsService;
			this.iconsFeature = options.dashboardApi.getFeature('Icons');
			this.conditionalFormatting = options.conditionalFormatting;

			this.dataProvider = new DataProvider();
			this.textStyleIndex = {
				cell: 'dataValueStyles',
				corner: 'valueHeadingStyles',
				row: 'rowHeadingStyles',
				column: 'columnHeadingStyles',
				summary: 'summaryStyles',
				selectedArea: 'selectedAreaStyles'
			};
			this._initializeGrid();
			this._shapeCols = [];
			this.rowHeightDeltas = new Map();
			this.columnWidthDeltas = new Map();
			this._getDataCellValue = CrosstabGridCellRenderers.getDataCellValue;
			this._isSummary = CrosstabGridCellRenderers.isSummary;
			this._buildResizeKey = CrosstabGridCellRenderers.buildResizeKey;
			this._isCellBelowInSummaryRow = CrosstabGridCellRenderers.isCellBelowInSummaryRow;
			this.renderedTheme = this.colorsService.getThemeDefinition().getId();
			this._measureHeaderDecorations = [];
			this._selectedMeasureNodes = [];
			this.gridCellSelection = new GridCellSelection({ domNode: this.$el[0] });
		}

		render(resultData, resizing, renderingNewData, eventHandler) {
			this.eventHandler = eventHandler;

			const slots = this.content.getFeature('Visualization').getSlots();
			//TODO: For now, setting the MeasuresAsRows feature flag means measures will be presented on the rows.
			// Once the property is implemented, the flag should not be part of this condition.
			const columnsSlotIsPopulated = slots.getSlot(Constants.EDGE_SLOT_IDS.COLUMN).getDataItemList().length > 0;
			const rowsSlotIsPopulated = slots.getSlot(Constants.EDGE_SLOT_IDS.ROW).getDataItemList().length > 0;
			const showValuesOnRowsProp = this.content.getPropertyValue('valuesAsRows');
			if (showValuesOnRowsProp === 'rowsAuto') {
				//We can only show values on the rows if the columns slot has something in it.
				this.showValuesOnRows = columnsSlotIsPopulated;
			} else {
				//We need to force it on if the rows slot is not populated....but NOT if neither rows or columns are populated.
				this.showValuesOnRows = (columnsSlotIsPopulated && !rowsSlotIsPopulated);
			}

			// If the data has changed or we're not resizing (property change) then create the pivot table
			if (!resizing || renderingNewData || !this.data) {
				this._pivotTable = new Pivot({
					data: resultData,
					content: this.content,
					slots: this.content.getFeature('Visualization').getSlots(),
					dashboard: this.dashboardApi,
					conditionalFormatting: this.conditionalFormatting,
					visFormatSpec: this.content.getPropertyValue('format'),
					showValuesOnRows: this.showValuesOnRows
				});
				this.data = this._pivotTable.create();

				// if the data has changed, cached cellWidthStatus needs to be reset
				this._cellWidthStatus = null;
				this._unMountVirtualGridIfNeeded(renderingNewData);
			}

			this._alternateRowFlag = -1; // -1: not initialize, 1: alternate on odd number, 0: alternate on even number.
			this._cellStartRow = this._pivotTable.colEdgeNestingLevelWithMeasure ? this._pivotTable.colEdgeNestingLevelWithMeasure : this._pivotTable.colEdgeNestingLevel;

			const currentDashboardTheme = this.colorsService.getThemeDefinition().getId();
			if (this.gridRef && resizing && !renderingNewData && this.renderedTheme === currentDashboardTheme) {
				//if only do resizing
				this.onResize();
				this.onSyncScrollBar();
				return Promise.resolve(true);
			} else {
				this.renderedTheme = currentDashboardTheme;
				// dataChange will change the rowNum and colNum of the data, need to re-render the grid
				this.dataProvider.updateData(this.data);
				//reset summary indicator flag
				if (!this._errorWarningAdded) {
					this.crosstabView.clearInfoIndicator('error_summary_cell');
				} else {
					this._errorWarningAdded = false;
				}
				this._preRenderReactGrid();
				this._renderReactGrid(renderingNewData);

				if (this.gridRef) {
					return Promise.resolve(true);
				} else {
					return Promise.resolve(false);
				}
			}
		}

		onResize() {
			this.maxColHeight = this._getMaxColHeight();
			this.gridRef && this.gridRef.recomputeGridSize();
		}

		_getMaxColHeight() {
			// Its a react virtual grid. Let the grid decide how much rows can fit in its space instead of using the this.$el size
			// return Infinity to allow react grid to calculate the number of max rows can fit in its height
			return Infinity;
		}

		onSyncScrollBar() {
			// when parent dom is moved or updated the scrollLeft and scrollTop value reset to 0 for the bottom-right-grid
			// this is a bug in React MultiGrid Level
			// the fix here is to sync the scrollBar among the three grid before onResize is triggered
			const $domNode = $(this.$el[0]);
			const rightBottomGrid = $domNode.find('.ba-common-grid__bottom-right-grid');
			const rightTopGrid = $domNode.find('.ba-common-grid__top-right-grid');
			const leftBottomGrid = $domNode.find('.ba-common-grid__bottom-left-grid');
			$(rightBottomGrid).animate({
				scrollLeft: $(rightTopGrid).scrollLeft(),
				scrollTop: $(leftBottomGrid).scrollTop()
			}, 0);
		}

		unmountVirtualGrid() {
			if (this.gridRef && this.$el) {
				ReactDOM.unmountComponentAtNode(this.$el[0]);
				this.gridRef = null;
			}
		}

		remove() {
			this.columnWidths = null;
			this.rowHeights = null;
			this.rowHeightDeltas = null;
			this.columnWidthDeltas = null;
			this.fixedRowHeight = null;
			this.fixedColumnWidth = null;
			this.hiddenRowsAndColumns = null;
			this._cachedHiddenCol = null;
			this._cachedHiddenRow = null;
			this._cachedRowDataItemInfo = null;
			this._cachedLevelInfo = null;
			this._cachedColumnDataItemInfo = null;
			this.isExpandable = null;
			this.isExpansionLineageHintSupported = null;
			this.dataProvider = null;
			this.data = null;
			this._pivotTable = null;
			this.eventHandler = null;
			this._selections = null;
			this._measureHeaderDecorations = null;
			this._selectedMeasureNodes = null;
			this._isSummaryHighlighted = null;
			this._cellWidthStatus = null;
			this._canvas = null;
			this.unmountVirtualGrid();
			this.gridCellSelection.remove();
			this.$el = null;
		}

		_setSummaryColor(gridCellProps) {
			const summaryColor = this.conditionalFormatting && this.conditionalFormatting.getSummaryFillColor();
			if (summaryColor) {
				_.extend(gridCellProps.style, { 'background-color': summaryColor });
			}
		}

		_getConditionalFormatting(condValue, dataValue, measureIndex) {
			let style;
			const measure = (measureIndex >= 0 && this._pivotTable.getNumberOfMeasures() > measureIndex) ? this._pivotTable._measuresInfo[measureIndex] : undefined;
			if (measure) {
				const dataItem = this.content.getFeature('Visualization').getSlots().getDataItem(measure.id);
				if(dataItem){
					style = this.conditionalFormatting.getFormatting(dataValue.rawValue, condValue.value, dataItem.getColumnId());
				}
			}
			return style;
		}

		restoreFormat(){
			this.fixedRowHeight = null;
			this.fixedColumnWidth = null;
			this.columnWidths = null;
			this.rowHeights = null;
			this.rowHeightDeltas = null;
			this.columnWidthDeltas = null;
		}

		setFormat(format){
			this.fixedRowHeight = format.fixedRowHeight;
			this.fixedColumnWidth = format.fixedColumnWidth;
			this.columnWidths = format.columnWidths;
			this.rowHeights = format.rowHeights;
			if (!this.rowHeights) {
				this.rowHeightDeltas = new Map();
			}
			if (!this.columnWidths) {
				this.columnWidthDeltas = new Map();
			}
		}

		setHiddenRowsAndColumns(hiddenRowsAndColumns){
			this.hiddenRowsAndColumns = hiddenRowsAndColumns;
		}

		/**
		 * Apply highlight decoration to the selected measure header cell
		 * @param $node: the node has been selected
		 */
		setMeasureHeaderDecorations(node){
			const decorations = $(node).attr('usevalue');
			if (this._measureHeaderDecorations.indexOf(decorations) > -1 || $(node).hasClass('dashboard-grid-cell-selected')){
				this.clearMeasureHeaderDecorations();
			} else {
				this._measureHeaderDecorations.push(decorations);
				this._selectedMeasureNodes.push(node);
			}
		}

		/**
		 * Get selected measure header cell nodes list
		 */
		getSelectedMeasureNodes(){
			return this._selectedMeasureNodes;
		}

		/**
		 * Clear selected measure header cell decoration.
		 */
		clearMeasureHeaderDecorations(){
			this._measureHeaderDecorations.length = 0;
			this._selectedMeasureNodes.length = 0;
		}

		highlightSummary(node){
			this._isSummaryHighlighted = !$(node).hasClass('dashboard-grid-cell-selected');
		}

		resetSummaryHighlight(){
			this._isSummaryHighlighted = false;
		}

		isSummaryHighlighted() {
			return this._isSummaryHighlighted;
		}

		/**
		 * calculate delta value bounded to minimal values
		 *
		 * @param {integer} delta
		 * @param {integer} bound, MIN_COLUMN_WIDTH or MIN_ROW_HEIGHT
		 * @param {integer} spanIndex
		 * @param {integer} currentValue, gridCellColumnWidth/gridCellRowHeight value
		 */
		_calculateDelta(delta, bound, spanIndex, currentValue) {
			if (delta + currentValue < bound) {
				delta = bound - currentValue;
			} else if (spanIndex > 0) {
				const evenDelta = parseInt((delta + currentValue) / (spanIndex + 1));
				if (evenDelta < bound) {
					delta = delta + (bound - evenDelta) * (spanIndex + 1);
				}
			}
			return delta;
		}

		/**
		 * resize the grid with draggable handler
		 *
		 * @param {object} dataProvider
		 * @param {string} direction, 'x' for columnWidth and 'y' for rowHeight
		 * @param {integer} delta
		 * @param {integer} colIndex
		 * @param {integer} rowIndex
		 * @param {integer} colSpan
		 * @param {integer} rowSpan
		 * @param {integer} fixedRows, numbers of fixed rows
		 * @param {integer} fixedColumns, numbers of fixed columns
		 * @param {integer} defaultValue, fixedColumnWidth/fixedRowHeight value
		 * @param {integer} currentValue, gridCellColumnWidth/gridCellRowHeight value
		 * @param {boolean} skipBound, skip the bound check and grow the cell if skipBound is set to true
		 */
		_onDraggableResize(options){
			if (options.delta !== undefined && options.delta === 0) {
				return;
			}
			const { cellInfo, gridInfo, dataProvider, direction, delta, rowIndex, colIndex, colSpan, rowSpan, fixedColumns, fixedRows, defaultValue, currentValue, skipBound } = options;
			let index, spanIndex, boundIndex, bound, formatMap, propertyName, rootCellValue, rootResizeKey, siblingCellValue, siblingResizeKey;
			let siblingCellKey;
			const dataValue = dataProvider.getValue(rowIndex, colIndex);
			if (direction === Constants.AXIS_X){
				formatMap = this.columnWidths || {};
				propertyName = 'columnWidths';
				index = colIndex;
				boundIndex = rowIndex;
				spanIndex = colSpan;
				bound = Constants.MIN_COLUMN_WIDTH;
				rootCellValue = dataProvider.getValue(fixedRows - 1, index);
				if (this._getDataCellValue(rootCellValue, 'type') === 'corner') {
					// when the cell is a corner cell, use the dataItem label as resize key
					rootResizeKey = this._getCachedDataItemInfoByIndex(this._cachedRowDataItemInfo, index, 'dataitemId');
				} else {
					rootResizeKey = this._buildResizeKey(this._getDataCellValue(rootCellValue, 'ancestor_or_self'));
				}
				siblingCellValue = dataProvider.getNumColumns() - 1 > index + 1 && dataProvider.getValue(fixedRows - 1, index + 1);
				siblingCellKey = `${fixedRows - 1} ${index + 1}`;
				if (siblingCellValue && this._getDataCellValue(siblingCellValue, 'type') === 'corner') {
					siblingResizeKey = this._getCachedDataItemInfoByIndex(this._cachedRowDataItemInfo, index + 1, 'dataitemId');
				} else {
					siblingResizeKey = this._buildResizeKey(this._getDataCellValue(siblingCellValue, 'ancestor_or_self'));
				}
			} else {
				formatMap = this.rowHeights || {};
				propertyName = 'rowHeights';
				index = rowIndex;
				boundIndex = colIndex;
				spanIndex = rowSpan;
				bound = Constants.MIN_ROW_HEIGHT;
				rootCellValue = dataProvider.getValue(index, fixedColumns - 1);
				if (this._getDataCellValue(rootCellValue, 'type') === 'corner') {
					// measure row is a special row since it does not have any dataItem mapped, use a constant as resize key
					rootResizeKey = this._getCachedDataItemInfoByIndex(this._cachedColumnDataItemInfo, index, 'dataitemId') || Constants.MEASURE_ROW_RESIZE_IDENTIFIER;
				} else {
					rootResizeKey = this._buildResizeKey(this._getDataCellValue(rootCellValue, 'ancestor_or_self'));
				}
				siblingCellValue = dataProvider.getNumRows() - 1 > index + 1 && dataProvider.getValue(index + 1, fixedColumns - 1);
				siblingCellKey = `${index + 1} ${fixedColumns - 1}`;
				if (siblingCellValue && this._getDataCellValue(siblingResizeKey, 'type') === 'corner') {
					siblingResizeKey = this._getCachedDataItemInfoByIndex(this._cachedColumnDataItemInfo, index + 1, 'dataitemId') || Constants.MEASURE_ROW_RESIZE_IDENTIFIER;
				} else {
					siblingResizeKey = this._buildResizeKey(this._getDataCellValue(siblingResizeKey, 'ancestor_or_self'));
				}
			}

			const resizedValue = delta + currentValue;
			const resizeKey = this._buildResizeKey(this._getDataCellValue(dataValue, 'ancestor_or_self'));
			formatMap[resizeKey] = resizedValue;
			formatMap[rootResizeKey] = resizedValue;

			// child resize will resize the sibling and the children
			let totalDelta = 0;
			if (!skipBound && boundIndex >= 0 && spanIndex <= 0 && siblingResizeKey) {
				formatMap[siblingResizeKey] = formatMap[siblingResizeKey] || defaultValue;
				const newFormatValue = Math.max(formatMap[siblingResizeKey] - delta, bound);
				const rootsOfResizedCell = gridInfo.expandedLeafMap.get(cellInfo.currentCellKey);
				const directRootOfResizeCell = rootsOfResizedCell && rootsOfResizedCell[rootsOfResizedCell.length - 1];
				const rootsOfSiblingCell = gridInfo.expandedLeafMap.get(siblingCellKey);
				const directRootOfSiblingCell = rootsOfSiblingCell && rootsOfSiblingCell[rootsOfSiblingCell.length - 1];
				// if currently
				// 1. resized cell and its sibling are sharing the same direct expansion root, or
				// 2. the sibling cell is actually the expansion of the resized cell
				// then the sibling cell may cancel the delta of the resized cell.
				const hasSameExpansionRoot = directRootOfResizeCell === directRootOfSiblingCell && directRootOfResizeCell;
				const siblingIsExpansionChild = directRootOfSiblingCell && JSON.parse(directRootOfSiblingCell).cellKey === cellInfo.currentCellKey;
				if(hasSameExpansionRoot || siblingIsExpansionChild){
					totalDelta = delta + (newFormatValue - formatMap[siblingResizeKey]);
				}else{
					totalDelta = delta;
				}
				formatMap[siblingResizeKey] = newFormatValue;
			}

			// root resize will resize the parent and the children
			if (spanIndex > 0){
				const evenDistribution = parseInt(resizedValue / (spanIndex + 1));
				let counter = spanIndex + 1;
				while (counter > 0) {
					if (direction === Constants.AXIS_X){
						rootResizeKey = this._buildResizeKey(this._getDataCellValue(dataProvider.getValue(fixedRows - 1, index + counter - 1), 'ancestor_or_self'));
					} else {
						rootResizeKey = this._buildResizeKey(this._getDataCellValue(dataProvider.getValue(index + counter - 1, fixedColumns - 1), 'ancestor_or_self'));
					}
					if (rootResizeKey) {
						formatMap[rootResizeKey] = evenDistribution;
					}
					counter--;
				}
			}

			if (cellInfo && cellInfo.scope === 'col' && gridInfo && gridInfo.expandedRootMap.has(cellInfo.currentCellKey)){
				if (this.columnWidthDeltas[resizeKey]){
					this.columnWidthDeltas[resizeKey] += (totalDelta - delta);
				} else {
					this.columnWidthDeltas[resizeKey] = (totalDelta - delta);
				}
			}
			if (cellInfo && gridInfo && gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)) {
				const roots = gridInfo.expandedLeafMap.get(cellInfo.currentCellKey);
				const directRoot = JSON.parse(roots[roots.length - 1]);
				const rootResizeKey = directRoot.resizeKey;
				const deltasMap = cellInfo.scope === 'col' ? this.columnWidthDeltas : this.rowHeightDeltas;
				if (deltasMap[rootResizeKey]){
					deltasMap[rootResizeKey] += totalDelta;
				}else{
					deltasMap[rootResizeKey] = totalDelta;
				}
			}
			this.crosstabView.setPropertyValue(propertyName,formatMap);
		}

		/**
		 * setup resize guideline and cell blocker for draggable resize handler
		 */
		_onStartResizeGuideLine(event, direction, containerClassName, offSetParent){
			let style = {};
			let guideLineClassName;
			if (direction === Constants.AXIS_X){
				guideLineClassName = 'grid-column-resizer-guideLine';
				style.height = this.$el[0].clientHeight;
			} else {
				guideLineClassName = 'grid-row-resizer-guideLine';
				style.width = this.$el[0].clientWidth;
			}
			// add cell blocker to make sure hover does not leak through the drag handler
			const cells = $(this.$el[0]).find('.' + containerClassName);
			_.each(cells, cell => {
				$(cell).addClass(containerClassName + '-resizing');
			});
			// initialize the resize guideline styles
			const resizeGuideLine = $(this.$el[0]).find('.xtab-grid-guideLine');
			const offset = $(this.$el[0]).find('.' + offSetParent).offsetParent().offsetParent().offset();
			$(resizeGuideLine).addClass(guideLineClassName);
			style.top = (event.clientY - offset.top - 1) + 'px';
			style.left = (event.clientX - offset.left - 1) + 'px';
			$(resizeGuideLine).css(style);
		}

		/**
		 * move resize guideline along with draggable resize handler
		 */
		_onDragResizeGuideLine(event, direction, offSetParent){
			const isResizingColumn = direction === Constants.AXIS_X;
			const guideLineClassName = isResizingColumn ? 'grid-column-resizer-guideLine' : 'grid-row-resizer-guideLine';
			// update guideLine position while dragging happens
			const resizeGuideLine = $(this.$el[0]).find('.' + guideLineClassName);
			const offset = $(this.$el[0]).find('.' + offSetParent).offsetParent().offsetParent().offset();
			if (isResizingColumn) {
				$(resizeGuideLine).css({
					left: (event.clientX - offset.left - 1) + 'px'
				});
			} else {
				$(resizeGuideLine).css({
					top: (event.clientY - offset.top - 1) + 'px'
				});
			}
		}

		/**
		 * remove resize guideline and cell blocker for draggable resize handler
		 */
		_onStopResizeGuideLine(event, direction, containerClassName){
			const isResizingColumn = direction === Constants.AXIS_X;
			const guideLineClassName = isResizingColumn ? 'grid-column-resizer-guideLine' : 'grid-row-resizer-guideLine';
			const style = isResizingColumn ? { height: '', top: '', left: '' } : { width: '', top: '', left: '' };
			// remove the cell blocker
			const cells = $(this.$el[0]).find('.' + containerClassName);
			_.each(cells, cell => {
				$(cell).removeClass(containerClassName + '-resizing');
			});
			// reset the guideLine styles
			const resizeGuideLine = $(this.$el[0]).find('.' + guideLineClassName);
			$(resizeGuideLine).css(style);
			$(resizeGuideLine).removeClass(guideLineClassName);
		}

		/**
		 * render the draggable resize handler
		 * @param {object} options
		 */
		_renderResizeHandler(options){
			const { cellInfo, gridInfo, dataProvider, direction, className, defaultValue, currentValue, containerClassName, offSetParent, rowIndex, colIndex, colSpan, rowSpan, fixedColumns, fixedRows, bound, skipBound } = options;
			let spanIndex = direction === Constants.AXIS_X ? colSpan : rowSpan;
			const resizeHandler = React.createElement('div', {
				className: className,
				onDoubleClick: () => {
					// double click to adjust the size fit the text
					let textWidth = direction === Constants.AXIS_X ? this._cellWidthStatus && this._cellWidthStatus[colIndex] && this._cellWidthStatus[colIndex]['textWidth'] || defaultValue : defaultValue;
					const isShapeCol  = this._shapeCols.includes(colIndex);
					if (isShapeCol) {
						textWidth += Constants.SHAPE_WIDTH;
					}
					let delta = textWidth - currentValue;
					if (spanIndex > 0) {
						delta = textWidth * spanIndex - currentValue;
					}
					if (delta > 0) {
						const draggableOptions = { dataProvider, direction, delta, gridInfo, rowIndex, cellInfo, colIndex, colSpan, rowSpan, fixedColumns, fixedRows, defaultValue, currentValue, skipBound };
						this._onDraggableResize(draggableOptions);
					}
				}
			}, '');
			return React.createElement(Draggable, {
				axis: direction,
				position: { x: 0, y: 0 },
				onStart: (event) => {
					this._onStartResizeGuideLine(event, direction, containerClassName, offSetParent);
				},
				onDrag: (event) => {
					this._onDragResizeGuideLine(event, direction, offSetParent);
				},
				onStop: (event, data) => {
					this._onStopResizeGuideLine(event, direction, containerClassName);
					const rawDeltaData = direction === Constants.AXIS_X ? data.x : data.y;
					let delta = this._calculateDelta(rawDeltaData, bound, spanIndex, currentValue);
					const draggableOptions = { cellInfo, gridInfo, dataProvider, direction, delta, rowIndex, colIndex, colSpan, rowSpan, fixedColumns, fixedRows, defaultValue, currentValue, skipBound };
					this._onDraggableResize(draggableOptions);
				}
			}, resizeHandler);
		}

		_renderGridCellContent(colIndex, style, fixedColumns, dataCellValue) {
			const shapeCol = colIndex + fixedColumns;
			let isShapeCol = this._shapeCols.includes(shapeCol);
			if (!isShapeCol && style.shapeId) {
				isShapeCol = !!this._shapeCols.push(shapeCol);
			}
			const contentSpec = {
				displayValue: this._wrapDataCellValue(colIndex + fixedColumns, dataCellValue, style, true, isShapeCol),
				iconsFeature: this.iconsFeature
			};
			if (isShapeCol) {
				contentSpec.shapeId = style.shapeId;
			}
			return <GridContentRenderer {...contentSpec}/>;
		}

		/**
		 * decorate edge cell with selection or implied selection style
		 * @param {string} valueType, Constants.EDGE_TYPE
		 * @param {object} dataValue, rawDataValue
		 * @param {string} useValue
		 * @param {array} classList, an array of class names
		 */
		_renderEdgeSelection(valueType, dataValue, useValue, classList){
			const sSlotID = this._getSlotIDByEdgeType(valueType);
			const ancestor_or_self = this._getDataCellValue(dataValue, 'ancestor_or_self');
			const edgeSelections = sSlotID === Constants.EDGE_SLOT_IDS.ROW ? this._selections.rowSelection : this._selections.colSelection;
			if (edgeSelections && edgeSelections.isSelected(ancestor_or_self)) {
				classList.push('dashboard-grid-cell-selected');
			} else {
				const selectedDataPoints = _.chain(_.pluck(this.crosstabView.getSelector().getSelections(), 'categories'))
					.flatten()
					.map(category => category.value)
					.value();
				if (selectedDataPoints.indexOf(useValue) !== -1) {
					classList.push('dashboard-grid-cell-selected');
				}
			}
		}

		/**
		 * decorate data cell with selection style
		 * @param {object} dataValue, rawDataValue
		 * @param {array} classList, an array of class names
		 */
		_renderDatapointSelection(dataValue, classList){
			let isSelected = false;
			const columnEdge = this._getDataCellValue(dataValue, 'columnEdge');
			const rowEdge = this._getDataCellValue(dataValue, 'rowEdge');
			if (columnEdge && rowEdge && _.isObject(rowEdge.value) && (columnEdge.isMeasure || _.isObject(columnEdge.value))) {
				const selections = this._selections.datapointSelection;
				if (selections) {
					const aColumnEdgeContext = !columnEdge.isMeasure ? this._getCurrentEdgeSelectionContext(columnEdge) : [];
					const aRowEdgeContext = this._getCurrentEdgeSelectionContext(rowEdge);
					const selected = selections.isSelected(aColumnEdgeContext.concat(aRowEdgeContext));
					if (selected) {
						classList.push('dashboard-grid-cell-selected');
						isSelected = true;
					}
				}
			}
			return isSelected;
		}

		_getSelectionFormattingStyles(useValue = ''){
			const selectedAreaStyles = this.textStyle[Constants.TEXT_STYLE_INDEX.SELECTED_AREA];
			let convertedStylesValue = {};
			let weight = '';
			_.each(selectedAreaStyles, (val, key) => {
				let formatKey = useValue;
				let propValue = val && val[formatKey];
				while(formatKey && formatKey.lastIndexOf(',') > 0){
					formatKey = formatKey.slice(0, formatKey.lastIndexOf(','));
					let nextFormatValue = val && val[formatKey];
					if (propValue && nextFormatValue && propValue.weight < nextFormatValue.weight || !propValue && nextFormatValue) {
						propValue = val[formatKey];
					}
				}
				//color prop need to convert the color into hexValue
				if(propValue && key === Constants.STYLE_CSS_MAP.Color){
					convertedStylesValue[key] = propValue.value && this.colorsService.getHexColorFromDashboardColorSet(propValue.value) || '';
				} else {
					convertedStylesValue[key] = propValue && propValue.value;
				}

				// keep the latest weight as reference
				let currentWeight= propValue && propValue.weight;
				if (currentWeight && currentWeight > weight){
					weight = currentWeight;
				}
			});
			if (_.values(convertedStylesValue).join('') !== '') {
				return { convertedStylesValue, weight };
			}
		}

		_getSelectionFormattingStylesForTuple(dataValue, tuple){
			const { columnEdgeUseValue, rowEdgeUseValue } = this._getEdgeUseValues(dataValue, tuple);
			const convertedStylesValueForColumnEdge = this._getSelectionFormattingStyles(columnEdgeUseValue);
			const convertedStylesValueForRowEdge = this._getSelectionFormattingStyles(rowEdgeUseValue);
			let convertedStyles = convertedStylesValueForColumnEdge || convertedStylesValueForRowEdge;
			if (convertedStylesValueForColumnEdge && convertedStylesValueForRowEdge){
				convertedStyles = convertedStylesValueForColumnEdge.weight > convertedStylesValueForRowEdge.weight ? convertedStylesValueForColumnEdge : convertedStylesValueForRowEdge;
			}
			return convertedStyles;
		}

		/**
		 * construct the edges useValues for dataPoint
		 * @param {object} dataValue
		 * @param {string} tuple value
		 */
		_getEdgeUseValues(dataValue, tuple) {
			const columnEdgeDecendant = dataValue.columnEdge && dataValue.columnEdge.descendant_or_self_without_measures;
			const columnEdgeCount = columnEdgeDecendant && columnEdgeDecendant.length || 0;
			const tupleUseValueList = tuple && _.isString(tuple) ? tuple.split(',') : null;
			const columnEdgeUseValue = tupleUseValueList && tupleUseValueList.slice(0, columnEdgeCount).join(',');
			const rowEdgeUseValue = tupleUseValueList && tupleUseValueList.slice(columnEdgeCount, tupleUseValueList.length).join(',');
			return { columnEdgeUseValue, rowEdgeUseValue };
		}

		rowEdgeHasMeasures() {
			const rowEdgeNestingLevel = this._pivotTable.rowEdgeNestingLevel;
			return this.showValuesOnRows && (this._pivotTable.getNumberOfMeasures() > 1 || rowEdgeNestingLevel === 0);
		}

		/**
		 * determine if a measure cell should be hidden
		 * measure does not have any useValue mapping, need to use the useValue of the parent row
		 * @param {object} dataProvider
		 * @param {integer} rowIndex
		 * @param {integer} columnIndex
		 */
		_isMeasureHidden(dataProvider, rowIndex, columnIndex) {
			if (rowIndex < 1) {
				return false;
			}
			const rowEdgeHasMeasures = this.rowEdgeHasMeasures();
			const dataValue = dataProvider.getValue(!rowEdgeHasMeasures ? rowIndex - 1: rowIndex, rowEdgeHasMeasures? columnIndex -1 : columnIndex);

			const useValue = this._getDataCellValue(dataValue, 'useValue');
			return this._isRowOrColumnHidden(useValue);
		}

		/**
		 * determine if a row or column should be hidden
		 * @param {string} useValue
		 */
		_isRowOrColumnHidden(useValue = '') {
			if (this.hiddenRowsAndColumns.length < 1) {
				return false;
			}
			let isHidden = this.hiddenRowsAndColumns.includes(useValue);
			while(useValue && useValue.lastIndexOf(',') > 0 && !isHidden){
				useValue = useValue.slice(0, useValue.lastIndexOf(','));
				isHidden = this.hiddenRowsAndColumns.includes(useValue);
			}
			return isHidden;
		}

		_getCanvasFont(style){
			// take default style into consideration
			const font = {
				'font-style': style[Constants.STYLE_CSS_MAP.FontItalic],
				'font-weight': style[Constants.STYLE_CSS_MAP.FontBold],
				'font-size': style[Constants.STYLE_CSS_MAP.FontSize] || Constants.DEFAULT_TEXT_STYLE.FONT_SIZE,
				'font-family': style[Constants.STYLE_CSS_MAP.FontFace] || Constants.DEFAULT_TEXT_STYLE.FONT_FAMILY
			};
			return _.values(font).join(' ');
		}

		_getTextWidth(text, font){
			this._canvas = this._canvas || document.createElement('canvas');
			let context = this._canvas.getContext('2d');
			context.font = font;
			return parseInt(context.measureText(text).width);
		}

		_generateWrappedText(colIndex, font, width){
			// cached the character counter that can fit a column, so we do not need to repeatly calculate for every cell in the same column
			const cachedCellWidthStatus = this._cellWidthStatus && this._cellWidthStatus[colIndex];
			const cachedCharCount = cachedCellWidthStatus && cachedCellWidthStatus['charCount'];
			const cachedCellWidth = cachedCellWidthStatus && cachedCellWidthStatus['cellWidth'];
			const wrappedText = '#';
			if(cachedCharCount && width === cachedCellWidth){
				return wrappedText.repeat(cachedCharCount);
			} else {
				const singleCharWidth = this._getTextWidth(wrappedText, font);
				// take padding into consideration
				let charCount = parseInt((width - Constants.DEFAULT_TEXT_STYLE.PADDING_WIDTH) / singleCharWidth);
				// when the cell width is super small, wrap at least one char
				charCount = charCount <= 0 ? 1 : charCount;
				this._cellWidthStatus = this._cellWidthStatus || {};
				this._cellWidthStatus[colIndex] = this._cellWidthStatus[colIndex] || {};
				this._cellWidthStatus[colIndex]['charCount'] = charCount;
				this._cellWidthStatus[colIndex]['cellWidth'] = width;
				return wrappedText.repeat(charCount);
			}
		}

		_wrapDataCellValue(colIndex, dataCellValue, gridCellStyles, isTextWrapped, isShapeCol){
			const font = this._getCanvasFont(gridCellStyles);
			// take padding into consideration
			const textWidth = this._getTextWidth(dataCellValue, font) + Constants.DEFAULT_TEXT_STYLE.PADDING_WIDTH;
			const cellWidth = parseInt(gridCellStyles.width);
			let cellContentWidth = textWidth;
			if (isShapeCol){
				cellContentWidth += Constants.SHAPE_WIDTH;
			}
			if (cellContentWidth > cellWidth){
				// cached the biggeset textWidth for resize to unwrap the text
				this._cellWidthStatus = this._cellWidthStatus || {};
				this._cellWidthStatus[colIndex] = this._cellWidthStatus[colIndex] || {};
				// we always keep track the biggest textWidth, so using colIndex as key is safe
				this._cellWidthStatus[colIndex]['textWidth'] = this._cellWidthStatus[colIndex]['textWidth'] < textWidth ? textWidth : this._cellWidthStatus[colIndex]['textWidth'] || textWidth;
				if (isTextWrapped){
					// only wrapped text if the flag is on
					dataCellValue = this._generateWrappedText(colIndex, font, cellWidth);
				}
			}
			return dataCellValue;
		}

		_getStylesOmittingWordWrap(styles){
			return _.omit(styles, Constants.STYLE_CSS_MAP.TextWrap);
		}

		_isSuppressionOn(){
			const suppressionValue = this.content && this.content.getPropertyValue(Constants.PROP_ID_SUPPRESSION);
			if (suppressionValue && suppressionValue === 'none') {
				return false;
			}
			return true;
		}

		_isExpandable() {
			return this.content && this.content.getPropertyValue(Constants.PROP_ID_EXPANDCOLLAPSE);
		}

		_isExpansionLineageHintSupported() {
			// By default, when rendering expanded members, we show the member's descendants as
			// "inside" the member's rectangle. This visual hint to help trace a member's lineage
			// is supported with arbitrarily-arranged DOM nodes (e.g. divs), but not with tables,
			// where cells cannot be superimposed, and must be rectangular.
			if (this.Grid.getRenderSupportFlag) {
				const isExpansionLineageHintSupported = this.Grid.getRenderSupportFlag('expansionLineageHintSupported');
				if(isExpansionLineageHintSupported !== undefined) {
					return isExpansionLineageHintSupported;
				}
			}
			return true;
		}

		/**
		 * get expandInfo from the dataItem
		 * @param {object} dataItem
		 * @param {object} expandMap
		 * @param {integer} maxDepth
		 * @returns {Object} maxDepth and expandMap
		 */
		_getExpandInfo(dataItem, expandMap, maxDepth) {
			const union = dataItem.getUnion();
			const dataItemList = union.getDataItemList();
			if (dataItemList.length > 0) {
				maxDepth++;
				dataItemList.forEach(newDataItem => {
					const nestedChildren = newDataItem.getDrillDownValue();
					expandMap[nestedChildren] = true;
					const nestedUnion = newDataItem.getUnion();
					const nestedDataItemList = nestedUnion.getDataItemList();
					if (nestedDataItemList.length > 0) {
						const tmpMaxDepth = this._getExpandInfo(newDataItem, expandMap, maxDepth).maxDepth;
						if (tmpMaxDepth > maxDepth) {
							maxDepth = tmpMaxDepth;
						}
					}
				});
			}
			return { maxDepth, expandMap };
		}

		/**
		 * get dataItemInfo by slotId
		 * @param {string} slotId
		 * @returns {Object} dataItemInfo
		 */
		_getDataItemInfoBySlotId(slotId) {
			const visualization = this.content && this.content.getFeature('Visualization');
			const slots = visualization && visualization.getSlots();
			const slot = slots.getSlot(slotId);
			const dataItemInfo = {};
			slot && slot.getDataItemList().forEach((dataItem, index) => {
				const { expandMap, maxDepth } = this._getExpandInfo(dataItem, {}, 0);
				dataItemInfo[index] = {
					dataitemId: dataItem.getColumnId(),
					dataitemUniqueId: dataItem.getId(),
					expandMap,
					maxDepth,
					isProperty: dataItem.getMetadataColumn().isProperty()
				};
			});
			return dataItemInfo;
		}

		/**
		 * get cached dataItemInfo by index
		 * @param {Object} cachedDataItemInfo
		 * @param {integer} row/column index
		 * @param {string} propertyName
		 * @returns {Object} dataItemInfo
		 */
		_getCachedDataItemInfoByIndex(cachedDataItemInfoMap, index, propertyName) {
			const cachedDataItemInfo = cachedDataItemInfoMap && cachedDataItemInfoMap[index];
			return propertyName ? cachedDataItemInfo && cachedDataItemInfo[propertyName] : cachedDataItemInfo;
		}

		_getCellPropertyColors() {
			try {
				// if user customized a background color for current widget
				const fillColor = this.content && this.content.getPropertyValue('fillColor');
				const cellFillColor = fillColor && this.colorsService.getHexColorFromDashboardColorSet(fillColor);
				// TODO: have our own theme mapping for new grid
				const foregroundColorProperties = this.colorsService.getForegroundColorPropertiesForUIElement(fillColor, 'tabs');
				const cellTextColor = foregroundColorProperties &&  foregroundColorProperties.length && foregroundColorProperties[0].value;
				return {
					cellFillColor,
					cellTextColor
				};
			} catch ( err ) {
				return {};
			}
		}

		_getIndentationDepth(dataItemInfo, type, index) {
			const maxDepth = this._getCachedDataItemInfoByIndex(dataItemInfo, index, 'maxDepth') || 0;
			const levelInfo = this._cachedLevelInfo[type][index];
			const maxLevelDepth = levelInfo && levelInfo.maxLevelDepth || 0;
			return Math.max(maxDepth, maxLevelDepth);
		}

		/**
		 * calculate columnWidth for current column
		 * hiddenColumnWidth > customizedColumnWidth > fixedColumnWidth(default)
		 * hiddenColumnWidth is set as a small float number that is close to 0 to prevent the react removing the dom with 0 width
		 * @param {object} dataProvider
		 * @param {integer} rowIndex
		 * @param {integer} columnIndex
		 * @param {integer} fixedColumnWidth the fixed column width provided by the virtual React grid component
		 */
		getColumnWidth(dataProvider, rowIndex, colIndex, fixedColumnWidth) {
			// check from cached status first
			if (this._cachedHiddenCol[colIndex]) {
				return Constants.HIDDEN_COL_WIDTH;
			}
			const fixedRowCellValue = dataProvider.getValue(rowIndex, colIndex);
			const isMeasure = this._getDataCellValue(fixedRowCellValue, 'isMeasure');
			if (this._isRowOrColumnHidden(this._getDataCellValue(fixedRowCellValue, 'useValue')) || (isMeasure && this._isMeasureHidden(dataProvider, rowIndex, colIndex))){
				this._cachedHiddenCol[colIndex] = true;
				return Constants.HIDDEN_COL_WIDTH;
			}
			// when the cell is a corner cell, use the dataItem label as resize key
			const rootResizeKey = this._getRootResizeKey(false, fixedRowCellValue, this._cachedRowDataItemInfo, colIndex);
			if (this.columnWidths && this.columnWidths[rootResizeKey]) {
				return this.columnWidths[rootResizeKey];
			}
			let isShapeCol = this._shapeCols.includes(colIndex);
			if (isShapeCol) {
				fixedColumnWidth += Constants.SHAPE_WIDTH;
			}
			// increase columnWidth for the expand indentations
			const indentationDepth = this._getIndentationDepth(this._cachedRowDataItemInfo, 'col', colIndex);
			if (this.isExpandable && indentationDepth > 0) {
				return parseInt(fixedColumnWidth, 10) + indentationDepth * Constants.EXPAND_INDENTATION;
			}
			return fixedColumnWidth;
		}

		/**
		 * calculate rowHeight for current row
		 * hiddenRowHeight > customizedRowHeight > fixedRowheight(default)
		 * hiddenRowHeight is set as a small float number that is close to 0 to prevent the react removing the dom with 0 height
		 * @param {object} dataProvider
		 * @param {integer} rowIndex
		 * @param {integer} columnIndex
		 * @param {integer} fixedRowHeight the fixed row height provided by the virtual React grid component
		 */
		getRowHeight(dataProvider, rowIndex, colIndex, fixedRowHeight) {
			// check from cached status first
			if (this._cachedHiddenRow[rowIndex]) {
				return Constants.HIDDEN_ROW_HEIGHT;
			}
			const fixedColumnCellValue = dataProvider.getValue(rowIndex, colIndex);
			let rowOrColHidden;
			if(this.showValuesOnRows) {
				const isMeasure = this._getDataCellValue(fixedColumnCellValue, 'isMeasure');
				rowOrColHidden = this._isRowOrColumnHidden(this._getDataCellValue(fixedColumnCellValue, 'useValue')) || (isMeasure && this._isMeasureHidden(dataProvider, rowIndex, colIndex));
			} else {
				rowOrColHidden = this._isRowOrColumnHidden(this._getDataCellValue(fixedColumnCellValue, 'useValue'));
			}
			if (rowOrColHidden){
				this._cachedHiddenRow[rowIndex] = true;
				return Constants.HIDDEN_ROW_HEIGHT;
			}
			// when the cell is a corner cell, use the dataItem label as resize key
			// measure row is a special row since it does not have any dataItem mapped, use a constant as resize key
			const rootResizeKey = this._getRootResizeKey(true, fixedColumnCellValue, this._cachedColumnDataItemInfo, rowIndex);
			if (this.rowHeights && this.rowHeights[rootResizeKey]) {
				return this.rowHeights[rootResizeKey];
			}
			// increase rowHeight for the expand indentations
			const indentationDepth = this._getIndentationDepth(this._cachedColumnDataItemInfo, 'row', rowIndex);
			if (this.isExpandable && indentationDepth > 0) {
				return parseInt(fixedRowHeight, 10) + indentationDepth * Constants.EXPAND_INDENTATION_HEIGHT;
			}
			return fixedRowHeight;
		}

		_getRootResizeKey(row, fixedCellValue, cachedDataItemInfo, index) {
			let ident = undefined;
			if(row) {
				ident = Constants.MEASURE_ROW_RESIZE_IDENTIFIER;
			}
			return this._getDataCellValue(fixedCellValue, 'type') === 'corner'
				? (this._getCachedDataItemInfoByIndex(cachedDataItemInfo, index, 'dataitemId') || ident)
				: this._buildResizeKey(this._getDataCellValue(fixedCellValue, 'ancestor_or_self'));
		}

		_renderReactGrid(renderingNewData) {

			let _dataProvider = this.dataProvider;
			_dataProvider.updateData.bind(this.dataProvider);
			_dataProvider.getNumColumns.bind(this.dataProvider);
			_dataProvider.getNumRows.bind(this.dataProvider);
			_dataProvider.getValue.bind(this.dataProvider);

			// in story, the first-time rendering from dashboard, the offsetHeight of the node is 0, need to check this
			this.maxColHeight = this._getMaxColHeight();

			let fixedColumns = this._pivotTable.getNestingLevels().rowNestingLevel;
			let fixedRows = this._pivotTable.getNestingLevels().colNestingLevel;
			_dataProvider.updateData(this.data);

			const numColumns = _dataProvider.getNumColumns() - fixedColumns;
			const numRows = _dataProvider.getNumRows() - fixedRows;

			const fixedRowHeight = this.fixedRowHeight || Constants.DEFAULT_ROW_HEIGHT;
			const fixedColumnWidth = this.fixedColumnWidth || Constants.DEFAULT_COLUMN_WIDTH;

			if(numRows === 0 && numColumns === 0) {
				fixedRows = fixedColumns = 0;
			}

			const isSuppressed = this._isSuppressionOn();
			this.isExpandable = this._isExpandable();
			this.isExpansionLineageHintSupported = this._isExpansionLineageHintSupported();
			// cache the levelInfo
			this._cachedLevelInfo = this._pivotTable.getLevelInfo();

			//reset cached hidden rows and columns info per render
			this._cachedHiddenRow = {};
			this._cachedHiddenCol = {};

			const gridInfo = {
				fixedRows,
				fixedColumns,
				numRows,
				numColumns,
				fixedColumnWidth,
				fixedRowHeight,
				isSuppressed,
				isSummaryHighlighted: this._isSummaryHighlighted,
				selections: this._selections,
				maxColHeight: this.maxColHeight,
				crosstabGrid: this,
				dataProvider: this.dataProvider,
				content: this.content,
				isExpandable: this.isExpandable,
				isExpansionLineageHintSupported: this.isExpansionLineageHintSupported,
				measureHeaderDecorations: this._measureHeaderDecorations,//pass in by reference so that gridInfo point to the same array instance
				hiddenRowsAndColumns: this.hiddenRowsAndColumns,
				conditionalFormatting: this.conditionalFormatting,
				gridDnDManager: this._gridDnDManager,
				pivotTable: this._pivotTable,
				// the set tells a cell is covered by another cell. The item in the set is in
				// "<row> <col>" format, like "32 4". If a cell is in this set, it means the cell
				// should be covered by another cell hence we don't need to render that cell at all.
				coveredCellSet: new Set(),
				// the key of this map is the root which is expanded, and its value is a set of direct or indirect leaves
				expandedRootMap: new Map(),
				// the key of this map is the leaf of an expanded root, and its value is an array of its direct or indirect ancestors. The last value is its direct ancestor
				expandedLeafMap: new Map(),
				renderedExpandedRootSet: new Set(),
				cellProps: new Map()
			};
			gridInfo.gridCellExpansionHelper = new GridCellExpansionHelper({ content: this.content, gridInfo, crosstabGridCellRenderers: CrosstabGridCellRenderers });
			// cache the dataitemInfo
			this._cachedRowDataItemInfo = gridInfo.gridCellExpansionHelper.getDataItemInfoBySlotId({ slotId: Constants.EDGE_SLOT_IDS.ROW });
			this._cachedColumnDataItemInfo = gridInfo.gridCellExpansionHelper.getDataItemInfoBySlotId({ slotId: Constants.EDGE_SLOT_IDS.COLUMN });
			this.gridInfo = gridInfo;


			const onAfterGridUpdate = (visibleRanges) => {
				gridInfo.coveredCellSet.clear();
				gridInfo.visibleRanges = visibleRanges;
				gridInfo.renderedExpandedRootSet.clear();
			};

			const dataCellRenderer = (rowIndex, colIndex, gridCellProps) => {
				return this._dataCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, numColumns, _dataProvider);
			};


			const fixedCornerCellRenderer = (rowIndex, colIndex, gridCellProps) => {
				return this._fixedCornerCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, _dataProvider);
			};

			// Virtual React grid component rowHeight property
			// The height of a row can be calculated by looking at the row height of the last header cell column
			const rowHeight = (rowIndex) => {
				// Returns a row height value. The value can be one of:
				// 1. default = 32 pixels
				// 2. hidden=very small value enough to render the row but not visibly enough to show the data in the row
				// 3. ResizeValue. This value is set the user resize the row height
				const lastFixedColIndex = fixedColumns - 1;
				return this.getRowHeight(_dataProvider, rowIndex, lastFixedColIndex, fixedRowHeight);
			};
			// Virtual React grid component columnWidth property
			// The width of a column can be calculated by looking at the column width of the last header cell row
			const columnWidth = (colIndex) => {
				// Returns a column width value. The value can be one of:
				// 1. default = 120 pixels
				// 2. hidden=very small value enough to render the column but not visibly enough to show the data in the row
				// 3. ResizeValue. This value is set the user resize the column height
				const lastFixedRowIndex = (fixedRows - 1 >= 0 ? fixedRows - 1 : 0);
				return this.getColumnWidth(_dataProvider, lastFixedRowIndex, colIndex, fixedColumnWidth);
			};

			// RTC #290279 - Crosstab and Grid will sometimes show blank cells when row span is active
			const overscanRowCount = ( fixedColumns > 1 ) ? 0 : 20;
			const overscanColumnCount = ( fixedRows > 1 ) ? 0 : 10;

			const style = {};
			const gridProperties = {
				rowHeight,
				columnWidth,
				numColumns,
				numRows,
				fixedRows,
				fixedColumns,
				onAfterGridUpdate,
				dataCellRenderer,
				fixedRowCellRenderer: CrosstabGridCellRenderers.getFixedCellRenderer({ gridInfo, isRowEdge: true, gridCellSelection: this.gridCellSelection }),
				fixedColumnCellRenderer: CrosstabGridCellRenderers.getFixedCellRenderer({ gridInfo, isRowEdge: false, gridCellSelection: this.gridCellSelection }),
				fixedCornerCellRenderer,
				overscanRowCount,
				overscanColumnCount,
				style,
				ref: (node) => { this.gridRef = node; },
				maxColumnWidth: Infinity,
				pageSize: PrintHelper.getPageSize(this.content),
				content: this.content,
				onCellFocus: this.gridCellSelection.setSelectedCell.bind(this.gridCellSelection),
				onScroll: ({ scrollLeft, scrollTop }) => {
					// coveredCellSet is use to calculate colspan and rowspan.
					// When scrolling rows/cells can be removed from the visible portion of the virtual grid which invalid the colspan and rowspan values
					// clear the set when scroll to force it to re-calculate
					gridInfo.coveredCellSet.clear();
					gridInfo.virtualGridScrolled = (scrollLeft > 0 || scrollTop > 0);
				}
			};
			if (renderingNewData){
				//When crosstab is updated with new data, the index of focused cell may become obsolete and surpass grid boundary, causing sporadic rendering issue.
				//So we need to reset focused cell with default value.
				const focusedCell = {
					columnIndex: -1,
					rowIndex: -1
				};
				gridProperties.focusedCell = focusedCell;
			}
			const crosstabGrid = this._getCrosstabGridComponentToMount(gridProperties);
			ReactDOM.render(crosstabGrid, this.$el[0]);

			if (this.gridRef) {
				this.gridRef.decorations = {
					measureHeaderDecorations: gridInfo.measureHeaderDecorations
				};
				this.gridRef.recomputeGridSize();
			}
		}

		_initializeGrid() {
			this.Grid = Toolkit.Grid;
			//crosstab dnd is only valid when render a crosstab on canvas
			// This class is also responsible for print crosstab in inView mode
			// When print crosstab in this mode do not instantiate gridDnDManager
			if (!PrintHelper.isInPrintLayoutMode(this.dashboardApi)) {
				this._gridDnDManager = new GridDndManager({
					grid: this,
					dashboardApi: this.dashboardApi,
					content: this.content,
					gridEdgeMappedToId: { row: Constants.EDGE_SLOT_IDS.ROW, column: Constants.EDGE_SLOT_IDS.COLUMN, values: Constants.EDGE_SLOT_IDS.VALUES }
				});
			}
		}

		_getCrosstabGridComponentToMount (gridProperties) {
			const grid = React.createElement(this.Grid, gridProperties);
			const gridGuideLine = React.createElement('div', { class: 'xtab-grid-guideLine' }, '');
			return React.createElement('div', { class: 'xtab-grid-view' }, [ grid, gridGuideLine ]);
		}

		/**
		 * @description Tell if a cell is a summary header or a summary data on a summary row
		 * @param {Object} dataValue
		 * @returns True if it's a row-summary cell, false otherwise.
		 */
		_isInSummaryRow(dataValue) {
			return dataValue && dataValue.rowEdge && dataValue.rowEdge.isSummary;
		}

		/**
		 * Set selections context which contains row edge selection, column edge selection
		 * and datapoint selection
		 **/
		setSelections(selections, options) {
			if(options && options.clearSelectionCaches){
				this.resetSummaryHighlight();
				this.clearMeasureHeaderDecorations();
			}
			this._selections = selections;
		}

		_getSlotIDByEdgeType(edgeType) {
			if (edgeType === Constants.EDGE_TYPE.ROW) {
				return Constants.EDGE_SLOT_IDS.ROW;
			} else if (edgeType === Constants.EDGE_TYPE.COLUMN) {
				return Constants.EDGE_SLOT_IDS.COLUMN;
			}
			return null;
		}

		setStyles(styleObj) {
			this.textStyle = styleObj;
		}

		_getCurrentEdgeSelectionContext(edge) {
			let aEdgeContextAncestor = _.clone(edge.ancestor_or_self);
			let aEdgeContextDescendant = _.clone(edge.descendant_or_self);
			aEdgeContextAncestor = _.filter(aEdgeContextAncestor, entry => entry.value);
			aEdgeContextAncestor.pop();
			aEdgeContextDescendant = _.filter(aEdgeContextDescendant, entry => entry.value);
			return (aEdgeContextAncestor || []).concat(aEdgeContextDescendant || []);
		}

		onEnterContainer() {
			const firstCellNode = $(this.$el[0]).find('.grid-corner-cell[row="0"][col="0"]');
			if (firstCellNode.length) {
				firstCellNode.focus();
			}
		}

		showDropZone(selector) {
			this.hideDropZones();
			const cells = this.$el.find(selector);
			cells.css({ 'display': 'block' });
			this.$el.find('.grid-column-resizer, .grid-row-resizer').css({ 'display': 'none' });  // Hide resizer handles
		}

		hideDropZones() {
			this.$el.find('.grid-drop-zone').css({ 'display': 'none' });
			this.$el.find('.grid-column-resizer, .grid-row-resizer').css({ 'display': 'block' }); // Show resizer handles
		}

		showReplaceDropZone(idx, type, useValue) {
			if ( idx === 'corner' ) {
				this.showDropZone(`.grid-corner-cell > .grid-drop-zone`);
			} else {
				if ( type === Constants.EDGE_TYPE.ROW ) {
					if ( useValue ) {
						this.showDropZone(`.grid-fixed-row-cell[row="${idx}"][useValue="${useValue}"] > .grid-drop-zone`);
					} else {
						this.showDropZone(`.grid-fixed-row-cell[row="${idx}"] > .grid-drop-zone`);
					}
				} else if ( type === 'col' ) {
					this.showDropZone(`.grid-fixed-column-cell[col="${idx}"] > .grid-drop-zone`);
				}
			}
		}

		showLeftDropZone(col) {
			if ( col === 'corner' ) {
				this.showDropZone(`.grid-corner-cell > .grid-drop-zone.left`);
			} else {
				this.showDropZone(`.grid-fixed-column-cell[col="${col}"] > .grid-drop-zone.left, .grid-fixed-row-cell[col="${col}"] > .grid-drop-zone.left`);
			}
		}

		showRightDropZone(col) {
			if ( col === 'corner' ) {
				this.showDropZone(`.grid-corner-cell > .grid-drop-zone.right`);
			} else {
				this.showDropZone(`.grid-fixed-column-cell[col="${col}"] > .grid-drop-zone.right, .grid-fixed-row-cell[col="${col}"] > .grid-drop-zone.right`);
			}
		}

		showTopDropZone(row, isMeasure) {
			if(isMeasure) {
				this.showDropZone(`.grid-fixed-column-cell[row="${row}"] > .grid-drop-zone.top`);
			} else {
				this.showDropZone(`.grid-fixed-row-cell[row="${row}"] > .grid-drop-zone.top`);
			}
		}

		showBottomDropZone(row, isMeasure) {
			if(isMeasure) {
				this.showDropZone(`.grid-fixed-column-cell[row="${row}"] > .grid-drop-zone.bottom`);
			} else {
				this.showDropZone(`.grid-fixed-row-cell[row="${row}"] > .grid-drop-zone.bottom`);
			}
		}

		_dataCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, numColumns, _dataProvider) {
			const currentRow = rowIndex + fixedRows;
			const currentCol = colIndex + fixedColumns;
			const dataValue = _dataProvider.getValue(currentRow, currentCol);
			const isSummary = this._isSummary(dataValue);
			const isCellBelowInSummaryRow = this._isCellBelowInSummaryRow(currentRow + 1, currentCol, _dataProvider, false);

			const { cellFillColor, cellTextColor } = this._getCellPropertyColors();

			let dataCellValue = this._getDataCellValue(dataValue);

			const rowEdgeHasMeasure = this.rowEdgeHasMeasures();
			const idx = rowEdgeHasMeasure ? rowIndex : colIndex;
			const measureIndex = (this._pivotTable.getNumberOfMeasures() > 0) ? idx % this._pivotTable.getNumberOfMeasures() : null;

			let formatSpec = Number.isInteger(measureIndex) ? this._pivotTable._measuresInfo[measureIndex].formatSpec : null;
			dataCellValue = Formatter.formatNull(dataCellValue, formatSpec);

			const tuple = this._getDataCellValue(dataValue, 'tuple');

			//populate the attributes we needed
			_.extend(gridCellProps, {
				tuple: tuple,
				measure: this._getDataCellValue(dataValue, 'isMeasure'),
				row: rowIndex + fixedRows,
				col: colIndex + fixedColumns,
				'aria-label': dataCellValue,
				tabindex: Constants.TABINDEX,
				area: Constants.AREA_TYPE.CELL
			});

			const classList = [gridCellProps.className];
			classList.push('grid-data-cell', 'dashboard-grid-cell');
			if (!isSummary) {
				this.gridCellSelection.addCellIsFocusedClass({ gridCellProps, classList });
			}

			if (this._cachedHiddenRow[rowIndex + fixedRows] || this._cachedHiddenCol[colIndex + fixedColumns]) {
				// if any of the edge is hidden, render an empty div
				gridCellProps.className = classList.join(' ');
				return React.createElement( 'div', gridCellProps, '' );
			}

			// set the z-index
			gridCellProps.style.zIndex = numColumns - colIndex;

			//render selection highlight
			const isDataPointSelected = this._renderDatapointSelection(dataValue,classList);

			// background color from Property
			if (cellFillColor && !isSummary){
				gridCellProps.style = _.extend(gridCellProps.style, {
					color: cellTextColor
				});
				if (!isDataPointSelected) {
					gridCellProps.style.backgroundColor = cellFillColor;
				}
			}

			//apply text style properties to this cell
			this._applyDataCellTextStyle(dataValue, gridCellProps);

			//apply Selection formatting styles to this cell
			this._applySelectionFormattingStyle(dataValue, tuple, gridCellProps);

			//Alternative row background
			if ((rowIndex + fixedRows) % 2 === 0) {
				classList.push('alternate');
			}

			//Conditional Formatting
			this._applyCondStyle(dataValue, measureIndex, isDataPointSelected, gridCellProps);

			const children = [];

			// styles overwritten for summary cell
			this._applySummaryStyle(isSummary, gridCellProps, classList);

			if (isCellBelowInSummaryRow && !isSummary){
				classList.push('above-summary-row');
			}

			children.push(this._renderGridCellContent(colIndex, gridCellProps.style, fixedColumns, dataCellValue));

			gridCellProps.style.height -= 1;

			this._applySummaryRowStyle(isSummary, dataValue, isCellBelowInSummaryRow, gridCellProps, classList);

			// apply width and height style
			this._applyWidthHeightStyle(currentRow, currentCol, _dataProvider.getNumRows(), _dataProvider.getNumColumns(), gridCellProps);

			gridCellProps.className = classList.join(' ');

			return React.createElement( 'div', gridCellProps, children );
		}

		_applyDataCellTextStyle(dataValue, gridCellProps) {
			const valueType = this._getDataCellValue(dataValue, 'type');
			if (!valueType) {
				// ignore text wrapping styles
				const values = Object.assign({},this.textStyle[Constants.TEXT_STYLE_INDEX.CELL]);
				Object.keys(values).forEach((key) => values[key] === '' && delete values[key]);
				Object.assign(gridCellProps.style, this._getStylesOmittingWordWrap(values));
			}
		}

		_applySelectionFormattingStyle(dataValue, tuple, gridCellProps) {
			const selectedAreaStyles = this._getSelectionFormattingStylesForTuple(dataValue, tuple);
			if(selectedAreaStyles && Object.keys(selectedAreaStyles).length > 0){
				// ignore text wrapping styles
				const values =selectedAreaStyles.convertedStylesValue;
				Object.keys(values).forEach((key) => values[key] === '' && delete values[key]);
				Object.assign(gridCellProps.style, this._getStylesOmittingWordWrap(values));
			}
		}

		_applyCondStyle(dataValue, measureIndex, isDataPointSelected, gridCellProps) {
			const condValue = this._getDataCellValue(dataValue, 'condValue');
			if (condValue) {
				const style = this._getConditionalFormatting(condValue, dataValue, measureIndex);
				_.extend(gridCellProps.style, style);
				// Remove conditional background colour since theme-able background colour take precedence when the cell is selected
				if (isDataPointSelected) {
					delete gridCellProps.style['backgroundColor'];
				}
			}
		}

		_applySummaryStyle(isSummary, gridCellProps, classList) {
			if (isSummary) {
				classList.push(Constants.AREA_TYPE.SUMMARY);
				gridCellProps.area = Constants.AREA_TYPE.SUMMARY;
				// ignore text wrapping styles
				_.extend(gridCellProps.style, this._getStylesOmittingWordWrap(this.textStyle[Constants.TEXT_STYLE_INDEX.SUMMARY]));
				if(this._isSummaryHighlighted){
					classList.push('dashboard-grid-cell-selected');
				}
				this._setSummaryColor(gridCellProps);
			}
		}

		_applySummaryRowStyle(isSummary, dataValue, isCellBelowInSummaryRow, gridCellProps, classList) {
			if (isSummary && this._isInSummaryRow(dataValue)) {
				classList.push('summary-row');
				classList.push('summary-row-top-border');
				if(!isCellBelowInSummaryRow){
					gridCellProps.style.height -= 1;
					classList.push('summary-row-bottom-border');
				}
			}
		}

		_applyWidthHeightStyle(currentRow, currentCol, numRows, numColumns, gridCellProps) {
			if (currentCol + 1 >= numColumns) {
				gridCellProps.style.width = parseFloat(gridCellProps.style.width) - 2 + 'px';
			}

			if (currentRow + 1 >= numRows) {
				gridCellProps.style.height -= 1;
			}
		}

		_fixedCornerCellRenderer(rowIndex, colIndex, gridCellProps, fixedRows, fixedColumns, _dataProvider) {
			const dataValue = _dataProvider.getValue(rowIndex, colIndex);
			let dataCellValue = this._getDataCellValue(dataValue);

			const fixedRowHeight = this.fixedRowHeight || Constants.DEFAULT_ROW_HEIGHT;
			const fixedColumnWidth = this.fixedColumnWidth || Constants.DEFAULT_COLUMN_WIDTH;

			//populate the attributes we needed
			_.extend(gridCellProps, {
				row: rowIndex,
				col: colIndex,
				tabindex: Constants.TABINDEX,
				area: Constants.AREA_TYPE.MEASURE,
				measure: 'true',
				'aria-label': dataCellValue
			});

			const classList = gridCellProps.className ? [gridCellProps.className] : [];
			classList.push('grid-corner-cell', 'dashboard-grid-cell');
			this.gridCellSelection.addCellIsFocusedClass({ gridCellProps, classList });

			//apply text style properties to this cell
			const valueType = this._getDataCellValue(dataValue, 'type');
			if (valueType) {
				const styleIndexForValueType = Constants.TEXT_STYLE_INDEX[valueType];
				_.extend(gridCellProps.style, this.textStyle[styleIndexForValueType]);
			}

			// colspan/rowspan
			const spanIndexOptions = {
				gridInfo: {
					dataProvider: _dataProvider, // Contains crosstab data that use to render the grid
					fixedRows, // Number of top most row headers
					fixedColumns, // Number of left most column headers
					fixedColumnWidth, // Default column width use to calculate colspan
					fixedRowHeight // Default row height use to calculate rowspan
				},
				cellInfo: {
					currentRow: rowIndex, // the value use to access a row in dataProvider
					currentCol: colIndex, // the value use to access a column in dataProvider
					rowEdgeMaxColumnIndex: fixedColumns, // For corner cells this is the maximum numnber of columns that corner cells are allow to have
					columnEdgeMaxRowIndex: fixedRows, // For corner cells this is the maximum number of rows the corner cells are allow to have
					dataCellValue, // The corner data cell value at currentRow and currentCol
					isSummary: false, // corner cells are not summary cells so set to false
					valueType // The type of corner cells. This vallue is equal to 'corner'
				},
				gridCellProps,
				crosstabGridCellRenderers: CrosstabGridCellRenderers,
				handlers: {
					getColumnWidth: this.getColumnWidth.bind(this),
					getRowHeight: this.getRowHeight.bind(this)
				}
			};
			const { colSpan, rowSpan, gridCellColumnWidth, gridCellRowHeight } = GridCellColRowSpanHelper.getSpanInfo(spanIndexOptions);
			_.extend(gridCellProps.style, {
				width: gridCellColumnWidth + 'px',
				height: Math.min(gridCellRowHeight, this.maxColHeight) + 'px',
				zIndex: (fixedColumns - colIndex) + (fixedRows - rowIndex) + (colSpan > rowSpan ? colSpan : rowSpan)
			});

			const children = [
				React.createElement( 'div', { class: 'dashboard-text-cell' }, dataCellValue )
			];

			if ( dataCellValue ) {
				children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
				children.push(React.createElement('div', { class: 'grid-drop-zone replace' }, ''));
				children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
			}

			gridCellProps.className = classList.join(' ');

			const container = this._gridDnDManager ? GridDropTarget : 'div';
			const element =  React.createElement(
				container,
				{ dndManager: this._gridDnDManager, isMeasure: true, cellType: 'corner', ...gridCellProps },
				children
			);
			this.addSpanInfo({ colSpan, rowSpan }, gridCellProps);
			return element;
		}

		_preRenderReactGrid() {
			this.$el.addClass('dashboard-common-grid').addClass('xtab-grid');
		}

		_unMountVirtualGridIfNeeded(renderingNewData) {
			if (renderingNewData && this.gridInfo) {
				const { isExpandable, virtualGridScrolled } = this.gridInfo || {};
				if (isExpandable && virtualGridScrolled) {
					// When topmost row headers get expanded the virtual grid always res12et the view back to the first column for the topmost row headers
					// When leftmost collumn headers get expanded the virtual grid DOES NOT reset the view back to the first row for the leftmost column headers
					// The simplest solution is to unmount/re-mount the virutal grid for expansion and scrolled
					// This way produce consistent results for both column and row headers and no gaps render on grid.
					// Note: The reason why for expansion is that the expanded cell need to be in the dom becuase it renders the expansion UI. If scrolled ouf of view
					// it does not get rendered producing white spaces (gaps) in the grid
					//
					// In future may look into retaining the scrolled location after expanding
					this.unmountVirtualGrid();
				}
			}
		}

		addSpanInfo(spanInfo, gridCellProps) {
			// Do nothing for crosstab render on canvas
			void(spanInfo);
			void(gridCellProps);
		}

		canResizeToWidget() {
			return true;
		}
	};

});
